import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { User, FileText, Activity, MapPin, Info, FileStack, AlertCircle } from 'lucide-react';
import { residencesService, careEnquiryService } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components';
import { ResidenceWizardSkeleton } from '../../../skeletons';

// Import our individual step components
import PersonalInfo from './PersonalInfo';
import FinanceDetails from './FinanceDetails';
import MedicalInfo from './MedicalInfo';
import ContactAddressInfo from './ContactAddressInfo';
import AllAboutMe from './AllAboutMe';
import Documentations from './Documentations';
import dummyClientData from '../../../data/dummyClient.json';

const steps = [
    { id: 1, title: 'Personal Info', icon: User },
    { id: 2, title: 'Finance', icon: FileText },
    { id: 3, title: 'Medical Info', icon: Activity },
    { id: 4, title: 'Contact / Address Info', icon: MapPin },
    { id: 5, title: 'All about me', icon: Info },
    { id: 6, title: 'Documentations', icon: FileStack },
];

/**
 * The 6-step client profile form, in three modes:
 *   edit    - /manager/residences/:id/edit
 *   convert - /manager/enquiries/convert, turning an ENQUIRY residence into a CLIENT
 *   create  - /manager/residences/new, a brand-new client with no enquiry behind
 *             it (the Residents list "Add" button). Nothing is written until the
 *             last step, so there is no residence id to work against until then.
 */
const ConvertClient = ({ createMode = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: paramId } = useParams();
    const { user } = useAuth();
    const [activeStep, setActiveStep] = useState(1);
    const [isLoading, setIsLoading] = useState(!!paramId);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    // Set when the form was autofilled from an Enquiries & Assessments record.
    const [prefillFrom, setPrefillFrom] = useState(null);

    // Mode detection: if paramId exists, we're editing. If not, we're converting from state.
    const isEditMode = !!paramId;
    const selectedIds = location.state?.selectedIds || [];
    // Create mode has no record yet, so no id to load or save against.
    const residenceId = createMode ? null : (isEditMode ? paramId : selectedIds[0]);
    // Domiciliary organisations call them clients, care homes call them residents.
    const personNoun =
        (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY' ? 'Client' : 'Resident';

    // Care home vs domiciliary — controls which finance variant is shown.
    const [residenceType, setResidenceType] = useState(null);

    const [formData, setFormData] = useState({
        personalDetails: {},
        financeDetails: {},
        medicalDetails: {},
        contactDetails: {},
        aboutMeDetails: {},
        documentations: {}
    });

    useEffect(() => {
        if (createMode) {
            // Nothing to fetch. The finance step still needs to know which
            // variant to show, and in create mode that comes from the
            // organisation the logged-in user belongs to.
            setResidenceType(personNoun === 'Client' ? 'DOMICILIARY' : 'CARE_HOME');
            return;
        }

        if (!residenceId) {
            navigate('/manager/enquiries');
            return;
        }

        if (isEditMode) {
            fetchResidenceData();
        } else {
            // Conversion mode: we keep the section data empty, but still need the
            // residence type so the finance step shows the correct variant.
            (async () => {
                try {
                    const response = await residencesService.getResidenceById(residenceId);
                    if (response.success && response.data) {
                        setResidenceType(response.data.residence_type);
                    }
                } catch (err) {
                    console.error('Error fetching residence type:', err);
                }
                // Autofill only the fields that match the returned assessment,
                // the admission plan and the original enquiry. Residences not
                // created through Enquiries & Assessments get null, and the
                // form stays empty exactly as before.
                try {
                    const prefill = await careEnquiryService.getConvertPrefill(residenceId);
                    if (prefill.success && prefill.data?.prefill) {
                        const p = prefill.data.prefill;
                        setFormData((prev) => ({
                            ...prev,
                            personalDetails: { ...p.personalDetails, ...prev.personalDetails },
                            financeDetails: { ...p.financeDetails, ...prev.financeDetails },
                            medicalDetails: { ...p.medicalDetails, ...prev.medicalDetails },
                            contactDetails: { ...p.contactDetails, ...prev.contactDetails },
                            aboutMeDetails: { ...p.aboutMeDetails, ...prev.aboutMeDetails },
                        }));
                        setPrefillFrom(prefill.data.reference || 'the assessment');
                    }
                } catch (err) {
                    console.error('Error fetching assessment prefill:', err);
                }
            })();
        }
    }, [residenceId, isEditMode, createMode, personNoun, navigate]);

    const fetchResidenceData = async () => {
        setIsLoading(true);
        try {
            const response = await residencesService.getResidenceById(residenceId);
            if (response.success && response.data) {
                const data = response.data;
                setResidenceType(data.residence_type);
                setFormData({
                    personalDetails: data.personalDetails || {},
                    financeDetails: data.financeDetails || {},
                    medicalDetails: data.medicalDetails || {},
                    contactDetails: data.contactDetails || {},
                    aboutMeDetails: data.aboutMeDetails || {},
                    documentations: data.documentations || {}
                });
            } else {
                setError(response.message || 'Failed to load residence data');
            }
        } catch (err) {
            console.error('Error fetching residence:', err);
            setError('An error occurred while loading residence data');
        } finally {
            setIsLoading(false);
        }
    };

    // Navigation handlers passed down to children
    const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, steps.length));
    const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 1));

    // Update form data for a specific section
    const updateSectionData = (section, data) => {
        setFormData(prev => ({
            ...prev,
            [section]: { ...prev[section], ...data }
        }));
    };

    const fillTestData = () => {
        setFormData(dummyClientData);
    };

    // Final submit handler for Step 6
    const handleSubmit = async () => {
        setIsSaving(true);
        setError('');
        try {
            let response;
            if (isEditMode) {
                response = await residencesService.updateResidence(residenceId, formData);
            } else if (createMode) {
                response = await residencesService.createClientDirect(formData);
            } else {
                response = await residencesService.convertEnquiryToClient(residenceId, formData);
            }

            if (response.success) {
                const newCode = response.data?.residence_code;
                alert(isEditMode
                    ? 'Residence details updated successfully!'
                    : `${personNoun} successfully ${createMode ? 'created' : 'converted'} and details saved!${newCode ? `\n${personNoun} ID: ${newCode}` : ''}`);
                navigate('/manager/residences');
            } else {
                setError(response.message || 'Failed to save residence details');
            }
        } catch (err) {
            console.error('Error in saving:', err);
            setError(err.message || 'An error occurred while saving');
        } finally {
            setIsSaving(false);
        }
    };

    // Function to render the correct step component based on activeStep. Steps
    // render fields only — Back / Next / Save are in the page header above.
    const renderStepContent = () => {
        const commonProps = {
            updateData: updateSectionData,
            data: formData,
            residenceType
        };

        switch (activeStep) {
            case 1:
                return <PersonalInfo {...commonProps} />;
            case 2:
                return <FinanceDetails {...commonProps} />;
            case 3:
                return <MedicalInfo {...commonProps} />;
            case 4:
                return <ContactAddressInfo {...commonProps} />;
            case 5:
                return <AllAboutMe {...commonProps} />;
            case 6:
                return <Documentations {...commonProps} />;
            default:
                return null;
        }
    };

    if (isLoading) {
        return <ResidenceWizardSkeleton />;
    }

    return (
        <div className="w-full py-4">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {isEditMode ? 'Edit Residence Profile' : (createMode ? `Add ${personNoun}` : 'Convert Enrolled User to Residence')}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                        Please complete all 6 steps to finish the profile setup
                    </p>
                </div>
                {/* Step navigation lives here, top-right — the step cards used to
                    end with their own Back / Next row, which the Ask Patch
                    launcher covered when it was pinned to the bottom corner. */}
                <div className="flex items-center gap-3">
                    {import.meta.env.VITE_SHOW_TEST_BUTTON === 'true' && (
                        <button
                            onClick={fillTestData}
                            className="px-4 py-2 bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 rounded-lg text-sm font-medium hover:bg-amber-100 transition shadow-sm"
                        >
                            Fill Test Data
                        </button>
                    )}
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </Button>
                    {activeStep > 1 && (
                        <Button variant="ghost" onClick={handleBack} disabled={isSaving}>
                            Back
                        </Button>
                    )}
                    {activeStep < steps.length ? (
                        <Button variant="primary" onClick={handleNext}>
                            Next
                        </Button>
                    ) : (
                        <Button variant="primary" loading={isSaving} onClick={handleSubmit}>
                            {isEditMode ? 'Update Residence Details' : `Complete & Save ${createMode ? personNoun : 'Client'}`}
                        </Button>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-lg p-4 flex gap-3 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {prefillFrom && !isEditMode && (
                <div className="mb-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/40 rounded-lg p-4 flex gap-3 text-blue-700 dark:text-blue-300">
                    <Info className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm font-medium">
                        Matching details were filled in from enquiry {prefillFrom} and its assessment. Please check them before saving.
                    </p>
                </div>
            )}

            {/* Top Stepper Navigation */}
            <div className="flex items-center justify-between overflow-x-auto pb-4 mb-6 scrollbar-hide">
                {steps.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = step.id === activeStep;
                    const isPast = step.id < activeStep;
                    const isLast = index === steps.length - 1;

                    return (
                        <React.Fragment key={step.id}>
                            {/* Step Icon and Title */}
                            <div
                                className="flex items-center gap-2 flex-shrink-0 cursor-pointer group"
                                onClick={() => setActiveStep(step.id)}
                            >
                                <Icon className={`w-5 h-5 transition-colors ${isActive || isPast ? 'text-blue-600' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600'}`} />
                                <span className={`text-[14px] font-semibold transition-colors ${isActive || isPast ? 'text-blue-600' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600'}`}>
                                    {step.title}
                                </span>
                            </div>

                            {/* Dashed / Solid connecting lines */}
                            {!isLast && (
                                <div className={`flex-1 min-w-[40px] mx-4 border-t-2 transition-colors duration-300 ${isActive || isPast ? 'border-solid border-blue-200' : 'border-dashed border-gray-200 dark:border-gray-700'}`}></div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Main Form Container - Renders the active child component */}
            <div className="bg-white dark:bg-[#262b33] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-8">
                {renderStepContent()}
            </div>

        </div>
    );
};

export default ConvertClient;